import { cva } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cn } from '../utils/cn';

type ButtonVariant = 'primary' | 'secondary';

type ButtonBaseProps = Omit<ComponentPropsWithRef<'button'>, 'children'> & {
    variant?: ButtonVariant;
    /** Icône lucide-react, passée comme composant : icon={RefreshCw}. */
    icon?: LucideIcon;
};

/**
 * Un bouton a toujours un nom accessible : soit un texte, soit (bouton icône seule) un
 * aria-label. Le typage rend l'oubli impossible.
 */
type ButtonProps = ButtonBaseProps &
    ({ children: ReactNode } | { children?: never; 'aria-label': string });

const buttonVariants = cva(
    [
        'inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900 dark:focus-visible:outline-white',
        'disabled:cursor-not-allowed disabled:opacity-50',
    ],
    {
        variants: {
            variant: {
                primary:
                    'bg-gray-900 text-white hover:bg-gray-700 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200',
                secondary:
                    'border border-gray-300 bg-white text-gray-900 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800',
            },
        },
        defaultVariants: { variant: 'primary' },
    },
);

/** Classes du bouton, réutilisables sur un lien qui doit en avoir l'apparence (<a href>). */
export function buttonClassName(variant: ButtonVariant = 'primary', className?: string) {
    return cn(buttonVariants({ variant }), className);
}

export default function Button({
    variant = 'primary',
    icon: Icon,
    type = 'button',
    className,
    children,
    ...props
}: ButtonProps) {
    return (
        // type="button" par défaut : un bouton dans un formulaire ne le soumet pas par accident.
        <button type={type} className={buttonClassName(variant, className)} {...props}>
            {/* Décorative : le nom du bouton vient du texte ou de l'aria-label. */}
            {Icon && <Icon aria-hidden="true" className="size-4 shrink-0" />}
            {children}
        </button>
    );
}
